import React, { useState } from 'react';
import './PayoutModal.css';
import './Disputes.css';

const LIFECYCLE_STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'awaiting_response', label: 'Awaiting Response' }
];

const RESOLUTION_TYPES = [
  { value: 'full_refund', label: 'Full refund' },
  { value: 'partial_refund', label: 'Partial refund' },
  { value: 'replacement', label: 'Replacement / remake' },
  { value: 'dismissed_no_fault', label: 'Dismissed — no fault found' },
  { value: 'other', label: 'Other' }
];

const STATUS_LABELS = {
  open: 'Open',
  under_review: 'Under Review',
  awaiting_response: 'Awaiting Response',
  resolved: 'Resolved',
  dismissed: 'Dismissed'
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric'
  });
};

const DisputeDetail = ({ dispute, onBack, onStatusChange, onResolve }) => {
  const isTerminal = dispute.status === 'resolved' || dispute.status === 'dismissed';

  const [status, setStatus] = useState(
    isTerminal ? 'under_review' : dispute.status || 'open'
  );
  const [notes, setNotes] = useState(dispute.admin_notes || '');
  const [resolutionType, setResolutionType] = useState('full_refund');
  const [resolutionAmount, setResolutionAmount] = useState(
    dispute.resolution_amount != null ? String(dispute.resolution_amount) : ''
  );
  const [finalStatus, setFinalStatus] = useState('resolved');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const evidence = Array.isArray(dispute.evidence_urls) ? dispute.evidence_urls : [];
  const amountNeeded = resolutionType === 'full_refund' || resolutionType === 'partial_refund';

  const handleSaveStatus = async () => {
    setSubmitting(true);
    setError('');
    try {
      await onStatusChange(dispute.id, status, notes.trim() || undefined);
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  const handleResolveSubmit = async (e) => {
    e.preventDefault();
    if (amountNeeded && !resolutionAmount.trim()) {
      setError('Enter the refund amount for this resolution type.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onResolve(dispute.id, {
        resolutionType,
        resolutionAmount: amountNeeded ? resolutionAmount.trim() : undefined,
        adminNotes: notes.trim() || undefined,
        finalStatus
      });
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="dispute-detail">
      {/* Header with back button */}
      <div className="dispute-detail-header">
        <button type="button" className="dispute-back-btn" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
          </svg>
          All disputes
        </button>
        <div className="dispute-detail-header-right">
          {dispute.report_number && (
            <span className="dispute-detail-rpt">{dispute.report_number}</span>
          )}
          <span className={`admin-pill dispute-pill-${dispute.status || 'open'}`}>
            {STATUS_LABELS[dispute.status] || 'Open'}
          </span>
        </div>
      </div>

      {/* Order */}
      <div className="modal-info">
        <div className="modal-info-row">
          <span>Order</span>
          <span className="modal-info-value">{dispute._orderTitle || 'Order'}</span>
        </div>
        {dispute._quoteNumber && (
          <div className="modal-info-row">
            <span>Quote</span>
            <span className="modal-info-value modal-ref">{dispute._quoteNumber}</span>
          </div>
        )}
        <div className="modal-info-row">
          <span>Order Total</span>
          <span className="modal-info-value modal-amount">
            ${Number(dispute._orderTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </span>
        </div>
        <div className="modal-info-row">
          <span>Reported</span>
          <span className="modal-info-value">{formatDate(dispute.created_at)}</span>
        </div>
      </div>

      {/* Parties */}
      <div className="modal-payout-info">
        <h4>Parties</h4>
        <div className="modal-payout-details">
          <div className="modal-detail-row">
            <span>Client</span>
            <span>{dispute._clientName || '—'}{dispute._clientEmail ? ` · ${dispute._clientEmail}` : ''}</span>
          </div>
          <div className="modal-detail-row">
            <span>Manufacturer</span>
            <span>{dispute._manufacturerName || '—'}{dispute._manufacturerEmail ? ` · ${dispute._manufacturerEmail}` : ''}</span>
          </div>
        </div>
      </div>

      {/* Issue */}
      <div className="modal-payout-info">
        <h4>Reported Issue</h4>
        <div className="modal-payout-details">
          <div className="modal-detail-row">
            <span>Category</span>
            <span>{dispute._categoryLabel || dispute.category}</span>
          </div>
          {dispute.issue_date && (
            <div className="modal-detail-row">
              <span>Issue date</span>
              <span>{formatDate(dispute.issue_date)}</span>
            </div>
          )}
        </div>
        <p className="dispute-description">{dispute.description || 'No description provided.'}</p>
      </div>

      {/* Evidence */}
      <div className="modal-payout-info">
        <h4>Evidence {evidence.length > 0 ? `(${evidence.length})` : ''}</h4>
        {evidence.length === 0 ? (
          <p className="dispute-no-evidence">No evidence uploaded.</p>
        ) : (
          <div className="dispute-evidence-grid">
            {evidence.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noreferrer" className="dispute-evidence-thumb">
                <img src={url} alt={`Evidence ${i + 1}`} />
              </a>
            ))}
          </div>
        )}
      </div>

      {isTerminal ? (
        /* Already resolved — read-only summary */
        <div className="modal-payout-info">
          <h4>Resolution</h4>
          <div className="modal-payout-details">
            <div className="modal-detail-row">
              <span>Outcome</span>
              <span>{(RESOLUTION_TYPES.find(r => r.value === dispute.resolution_type) || {}).label || dispute.resolution_type || '—'}</span>
            </div>
            {dispute.resolution_amount != null && (
              <div className="modal-detail-row">
                <span>Amount</span>
                <span>${Number(dispute.resolution_amount).toFixed(2)}</span>
              </div>
            )}
            <div className="modal-detail-row">
              <span>Resolved</span>
              <span>{formatDate(dispute.resolved_at)}</span>
            </div>
          </div>
          {dispute.admin_notes && <p className="dispute-description">{dispute.admin_notes}</p>}
        </div>
      ) : (
        <div className="dispute-detail-actions">
          {/* Admin notes (shared) */}
          <div className="modal-field">
            <label>Admin Notes (internal / included in resolution email)</label>
            <textarea
              placeholder="Notes about this dispute..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Triage: update status without resolving */}
          <div className="dispute-section">
            <h4 className="dispute-section-title">Triage</h4>
            <div className="dispute-inline-row">
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                {LIFECYCLE_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <button
                type="button"
                className="modal-cancel dispute-status-btn"
                onClick={handleSaveStatus}
                disabled={submitting}
              >
                Update Status
              </button>
            </div>
            <p className="dispute-hint">Updates status only — no email sent to the parties.</p>
          </div>

          {/* Resolve */}
          <form onSubmit={handleResolveSubmit} className="dispute-section dispute-resolve-form">
            <h4 className="dispute-section-title">Resolve Dispute</h4>
            <div className="modal-field">
              <label>Outcome</label>
              <select value={resolutionType} onChange={(e) => setResolutionType(e.target.value)}>
                {RESOLUTION_TYPES.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>

            {amountNeeded && (
              <div className="modal-field">
                <label>Refund Amount (USD) — record only</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={resolutionAmount}
                  onChange={(e) => setResolutionAmount(e.target.value)}
                />
              </div>
            )}

            <div className="modal-field">
              <label>Final Status</label>
              <select value={finalStatus} onChange={(e) => setFinalStatus(e.target.value)}>
                <option value="resolved">Resolved (upheld)</option>
                <option value="dismissed">Dismissed (not upheld)</option>
              </select>
            </div>

            <p className="dispute-hint">
              Both the client and manufacturer will be emailed the outcome. Refunds are recorded only — move the money manually.
            </p>

            {error && <div className="modal-error">{error}</div>}

            <div className="modal-actions">
              <button type="button" className="modal-cancel" onClick={onBack}>Cancel</button>
              <button type="submit" className="modal-submit" disabled={submitting}>
                {submitting ? 'Saving...' : 'Resolve & Notify'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default DisputeDetail;
